import { useState, useEffect, useRef, useCallback } from 'react';
import {
  ArrowLeftRight, Send, Square, Bot, User,
  ClipboardList, AlertTriangle, Loader2, Database, RefreshCw,
} from 'lucide-react';
import '../../tailwindcss/Tailwind.css';
import { buildApiUrl } from '../../services/apiConfig';

const API = (path) => buildApiUrl('NOVA_INGESTION', path);

function SchemaChangesPanel({ sourceId }) {
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!sourceId) return;
    setLoading(true);
    try {
      const res = await fetch(API(`/transfer/schema-changes?source_id=${sourceId}`), { headers: { Authorization: 'Bearer dev-token' } });
      const data = await res.json();
      setChanges(data.changes || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [sourceId]);

  useEffect(() => { load(); }, [load]);

  if (!sourceId) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-yellow-500" /> Schema Changes
        </h3>
        <button onClick={load} className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 border border-gray-200 rounded-md text-gray-600 hover:bg-gray-50 transition-colors">
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin text-primary" /> Loading...
        </div>
      ) : changes.length === 0 ? (
        <p className="text-sm text-gray-500">No pending schema changes.</p>
      ) : changes.map((c, i) => (
        <div key={i} className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg mb-2">
          <p className="text-sm font-medium text-gray-900">{c.object_path}</p>
          <p className="text-xs text-yellow-700 mt-0.5">{c.change_type}: {c.description}</p>
        </div>
      ))}
    </div>
  );
}

function TransferHistoryPanel({ sourceId }) {
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!sourceId) return;
    fetch(API(`/transfer/history?source_id=${sourceId}`), { headers: { Authorization: 'Bearer dev-token' } })
      .then(r => r.json()).then(d => setHistory(d.transfers || [])).catch(() => {});
  }, [sourceId]);

  if (!history.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
      <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-3">
        <ClipboardList className="h-4 w-4 text-gray-400" /> Recent Transfers
      </h3>
      <div className="divide-y divide-gray-100">
        {history.slice(0, 5).map((t, i) => (
          <div key={i} className="py-2 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-800">{t.source_object || t.object_path}</p>
              <p className="text-xs text-gray-400">{new Date(t.created_at).toLocaleString()}</p>
            </div>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              t.status === 'completed' ? 'bg-green-100 text-green-700'
              : t.status === 'failed' ? 'bg-red-100 text-red-700'
              : 'bg-yellow-100 text-yellow-700'
            }`}>{t.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChatPanel() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const chatEndRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || streaming) return;
    const text = input.trim(); setInput('');
    const sid = sessionId || `sess-${Date.now()}`;
    if (!sessionId) setSessionId(sid);

    const assistantIdx = messages.length + 1;
    setMessages(prev => [...prev, { role: 'user', content: text }, { role: 'assistant', content: '' }]);
    setStreaming(true);

    try {
      const ctrl = new AbortController(); abortRef.current = ctrl;
      const res = await fetch(API('/transfer/chat'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer dev-token' },
        body: JSON.stringify({ message: text, session_id: sid }),
        signal: ctrl.signal,
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder(); let buf = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split('\n'); buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const evt = JSON.parse(line.slice(6));
            if (evt.type === 'text' || evt.type === 'content') {
              setMessages(prev => prev.map((m, i) => i === assistantIdx ? { ...m, content: m.content + (evt.content || '') } : m));
            } else if (evt.type === 'tool_use') {
              setMessages(prev => prev.map((m, i) => i === assistantIdx ? { ...m, content: m.content + `\n\n[Tool: ${evt.tool}]` } : m));
            } else if (evt.type === 'done' && evt.session_id) {
              setSessionId(evt.session_id);
            } else if (evt.type === 'error') {
              setMessages(prev => prev.map((m, i) => i === assistantIdx ? { ...m, content: m.content + `\nError: ${evt.message}` } : m));
            }
          } catch { /* ignore */ }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError')
        setMessages(prev => prev.map((m, i) => i === assistantIdx ? { ...m, content: m.content || 'Connection error. Please try again.' } : m));
    } finally { setStreaming(false); }
  };

  const handleKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } };

  return (
    <div className="flex flex-col h-full">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 bg-gray-50">
        {messages.length === 0 && (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <div className="p-5 bg-primaryLight rounded-full mb-5">
              <ArrowLeftRight className="h-10 w-10 text-primary" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Data Transfer Agent</h2>
            <p className="text-sm text-gray-500 max-w-md mb-6">Ask me to discover sources, plan data transfers, execute pipelines, or check for schema changes.</p>
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
            <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${msg.role === 'user' ? 'bg-primary' : 'bg-gradient-to-br from-blue-500 to-purple-600'}`}>
              {msg.role === 'assistant' ? <Bot className="h-4 w-4 text-white" /> : <User className="h-4 w-4 text-white" />}
            </div>
            <div className={`max-w-[80%] p-3 rounded-lg text-sm ${msg.role === 'assistant' ? 'bg-white border border-gray-200 text-gray-800 rounded-bl-none' : 'bg-primary text-white rounded-br-none'}`}>
              {msg.content ? (
                <p className="whitespace-pre-wrap">{msg.content}</p>
              ) : (streaming && i === messages.length - 1 && msg.role === 'assistant') ? (
                <div className="flex gap-1.5">
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              ) : null}
            </div>
          </div>
        ))}
        <div ref={chatEndRef} />
      </div>

      {/* Input */}
      <div className="px-5 py-4 border-t border-gray-200 bg-white shrink-0">
        <div className="flex gap-2">
          <input
            value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKey}
            placeholder="e.g. 'Plan a transfer from source A to Postgres...'"
            disabled={streaming}
            className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary disabled:opacity-50"
          />
          {streaming ? (
            <button onClick={() => { abortRef.current?.abort(); setStreaming(false); }}
              className="flex items-center gap-1.5 px-4 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm">
              <Square className="h-4 w-4" /> Stop
            </button>
          ) : (
            <button onClick={sendMessage} disabled={!input.trim()}
              className="px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primaryDark disabled:opacity-40 transition-colors flex items-center gap-1.5">
              <Send className="h-4 w-4" />
            </button>
          )}
        </div>
        <p className="text-[10px] text-gray-400 mt-1.5 text-center">Enter to send</p>
      </div>
    </div>
  );
}

export default function DataTransfer() {
  const [activeSourceId, setActiveSourceId] = useState(null);
  const [sources, setSources] = useState([]);

  useEffect(() => {
    fetch(API('/sources'), { headers: { Authorization: 'Bearer dev-token' } })
      .then(r => r.json()).then(d => setSources(d.sources || d || [])).catch(() => {});
  }, []);

  return (
    <div className="flex flex-col h-full">
      <p className="px-6 pt-4 pb-0 text-[0.8rem] italic text-gray-600">Select a source or use the agent chat</p>
      <div className="flex-1 p-4 overflow-hidden">
        <div className="flex h-full bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      {/* Sidebar */}
      <div className="w-64 border-r border-gray-200 flex flex-col shrink-0">
        <div className="px-4 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-primary flex items-center gap-2">
            <Database className="h-5 w-5" /> Sources
          </h2>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-1">
          {sources.map(s => (
            <button key={s.id} onClick={() => setActiveSourceId(s.id)}
              className={`w-full text-left p-3 rounded-lg border transition-colors flex items-center gap-3 ${
                activeSourceId === s.id
                  ? 'border-primary/30 bg-primaryLight text-primary'
                  : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
              }`}>
              <Database className="h-4 w-4 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{s.name}</p>
                <p className="text-xs text-gray-400 truncate">{s.connector_type || s.source_type}</p>
              </div>
            </button>
          ))}
          {!sources.length && <p className="text-xs text-gray-400 px-1 py-2">No sources found. Connect a data source first.</p>}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {activeSourceId && (
          <div className="overflow-y-auto max-h-72 p-4 bg-white border-b border-gray-200 space-y-0">
            <SchemaChangesPanel sourceId={activeSourceId} />
            <TransferHistoryPanel sourceId={activeSourceId} />
          </div>
        )}
        <div className="flex-1 overflow-hidden">
          <ChatPanel />
        </div>
      </div>
        </div>
      </div>
    </div>
  );
}
